import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { lazy, Suspense } from "react";
import { HelmetProvider } from 'react-helmet-async';
import { LanguageProvider } from './context/LanguageContext';
import { AuthProvider } from './context/AuthContext';
import { IntercomProvider } from './components/providers/IntercomProvider';
import GoogleAnalyticsProvider from './components/providers/GoogleAnalyticsProvider';
import ApmProvider from './components/providers/ApmProvider';
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import Login from "./pages/Login";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import ThirdPartyCallback from "./components/auth/ThirdPartyCallback";
import { GoogleOAuthProvider } from '@/components/providers/GoogleOAuthProvider';
import { AppleAuthProvider } from '@/components/providers/AppleAuthProvider';
import ProfileCheck from "./components/auth/ProfileCheck";
import ScrollToTop from "./components/ScrollToTop";
import './i18n'; // Import i18n configuration

// Lazy load non-critical routes for better initial load performance
const IntroPage = lazy(() => import("./pages/IntroPage"));
const Online = lazy(() => import("./pages/Online"));
const Offline = lazy(() => import("./pages/Offline"));
const Library = lazy(() => import("./pages/Library"));
const Community = lazy(() => import("./pages/Community"));
const Author = lazy(() => import("./pages/Author"));
const WhatIsMysteryAct = lazy(() => import("./pages/WhatIsMysteryAct"));
const LoadingDemo = lazy(() => import("./pages/LoadingDemo"));
const BecomeStoreOwner = lazy(() => import("./pages/BecomeStoreOwner"));
const Stores = lazy(() => import("./pages/Stores"));
const StoreDetail = lazy(() => import("./pages/StoreDetail"));
const ScriptDetail = lazy(() => import("./pages/ScriptDetail"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogDetail = lazy(() => import("./pages/BlogDetail"));
const HelpCenter = lazy(() => import("./pages/HelpCenter"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const Careers = lazy(() => import("./pages/Careers"));
const GameMaster = lazy(() => import("./pages/GameMaster"));

// PDF preview pages
const PDF1 = lazy(() => import("./pages/pdf/PDF1"));
const PDF2 = lazy(() => import("./pages/pdf/PDF2"));
const PDF3 = lazy(() => import("./pages/pdf/PDF3"));
const PDF4 = lazy(() => import("./pages/pdf/PDF4"));
const PDF5 = lazy(() => import("./pages/pdf/PDF5"));

// Privacy policy and agreement pages
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const PrivacyCollectionNotice = lazy(() => import("./pages/PrivacyCollectionNotice"));
const PlayerAgreement = lazy(() => import("./pages/PlayerAgreement"));
const GameMasterAgreement = lazy(() => import("./pages/GameMasterAgreement"));
const AuthorAgreement = lazy(() => import("./pages/AuthorAgreement"));

// Profile related pages
const Profile = lazy(() => import("./pages/profile/Profile"));
const ProfileSettings = lazy(() => import("./pages/profile/ProfileSettings"));
const ProfileGames = lazy(() => import("./pages/profile/ProfileGames"));
const ProfileOrders = lazy(() => import("./pages/profile/ProfileOrders"));

const queryClient = new QueryClient();

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <HelmetProvider>
        <LanguageProvider>
          <BrowserRouter>
            <ScrollToTop />
            <GoogleOAuthProvider>
              <AppleAuthProvider>
                <AuthProvider>
                  <IntercomProvider>
                    <GoogleAnalyticsProvider>
                      <ApmProvider>
                        <TooltipProvider>
                          <Toaster />
                          <Sonner />
                          <ProfileCheck />
                          <Suspense fallback={<div className="h-screen w-full flex items-center justify-center bg-black"><div className="animate-pulse text-anzir text-xl">Loading...</div></div>}>
                            <Routes>
                              <Route path="/intro" element={<IntroPage />} />
                              <Route path="/home" element={<Index />} />
                              <Route path="/" element={<Index />} />
                              <Route path="/online" element={<Online />} />
                              <Route path="/offline" element={<Offline />} />
                              <Route path="/library" element={<Library />} />
                              <Route path="/community" element={<Community />} />
                              <Route path="/blog" element={<Blog />} />
                              <Route path="/blog/:id" element={<BlogDetail />} />
                              <Route path="/about" element={<AboutUs />} />
                              <Route path="/careers" element={<Careers />} />
                              <Route path="/help" element={<HelpCenter />} />
                              <Route path="/author" element={<Author />} />
                              <Route path="/host" element={<GameMaster />} />
                              <Route path="/what-is-mysteryact" element={<WhatIsMysteryAct />} />
                              <Route path="/loading-demo" element={<LoadingDemo />} />
                              <Route path="/store" element={<BecomeStoreOwner />} />
                              <Route path="/stores" element={<Stores />} />
                              <Route path="/store/:storeId" element={<StoreDetail />} />
                              <Route path="/script/:stableId/:supplyId" element={<ScriptDetail />} />
                              <Route path="/login" element={<Login />} />

                              {/* PDF preview routes */}
                              <Route path="/pdf/pdf1" element={<PDF1 />} />
                              <Route path="/pdf/pdf2" element={<PDF2 />} />
                              <Route path="/pdf/pdf3" element={<PDF3 />} />
                              <Route path="/pdf/pdf4" element={<PDF4 />} />
                              <Route path="/pdf/pdf5" element={<PDF5 />} />

                              {/* Profile routes (protected) */}
                              <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                              <Route path="/profile/settings" element={<ProtectedRoute><ProfileSettings /></ProtectedRoute>} />
                              <Route path="/profile/games" element={<ProtectedRoute><ProfileGames /></ProtectedRoute>} />
                              <Route path="/profile/orders" element={<ProtectedRoute><ProfileOrders /></ProtectedRoute>} />

                              <Route path="/auth/callback" element={<ThirdPartyCallback />} />
                              <Route path="/auth/google/callback" element={<ThirdPartyCallback />} />
                              <Route path="/auth/apple/callback" element={<ThirdPartyCallback />} />
                              
                              {/* Privacy policy and agreement routes */}
                              <Route path="/agreement/privacy-policy" element={<PrivacyPolicy />} />
                              <Route path="/agreement/privacy-collection-notice" element={<PrivacyCollectionNotice />} />
                              <Route path="/agreement/player" element={<PlayerAgreement />} />
                              <Route path="/agreement/game-master" element={<GameMasterAgreement />} />
                              <Route path="/agreement/author" element={<AuthorAgreement />} />

                              <Route path="*" element={<NotFound />} />
                            </Routes>
                          </Suspense>
                        </TooltipProvider>
                      </ApmProvider>
                    </GoogleAnalyticsProvider>
                  </IntercomProvider>
                </AuthProvider>
              </AppleAuthProvider>
            </GoogleOAuthProvider>
          </BrowserRouter>
        </LanguageProvider>
      </HelmetProvider>
    </QueryClientProvider>
  );
}

export default App;
